import abandonedCart from "./defs/abandoned-cart-recovery";
import agencyOnboarding from "./defs/agency-client-onboarding";
import agencyRetention from "./defs/agency-retention-reporting";
import appointmentReminders from "./defs/appointment-reminders";
import backInStock from "./defs/back-in-stock-waitlist";
import birthdayClub from "./defs/birthday-loyalty-club";
import churnSave from "./defs/churn-save-cancellation";
import courseOnboarding from "./defs/course-student-onboarding";
import discoveryCall from "./defs/discovery-call-application";
import dunning from "./defs/failed-payment-dunning";
import estimateToJob from "./defs/estimate-to-job-won";
import leadMagnet from "./defs/lead-magnet-nurture";
import newsletterWelcome from "./defs/newsletter-welcome-series";
import postPurchase from "./defs/post-purchase-review-request";
import productLaunch from "./defs/product-launch-runway";
import quizFunnel from "./defs/quiz-funnel-segmentation";
import realEstate from "./defs/real-estate-buyer-nurture";
import referralProgram from "./defs/referral-partner-program";
import reviewReferral from "./defs/review-referral-engine";
import saasTrial from "./defs/saas-trial-onboarding";
import speedToLead from "./defs/speed-to-lead-callback";
import tradeShow from "./defs/trade-show-lead-followup";
import trialToDemo from "./defs/trial-to-demo-sales-assist";
import webinar from "./defs/webinar-funnel";
import winback from "./defs/winback-reengagement";
import workshopTickets from "./defs/workshop-ticket-sales";
import type { TemplateCategory, TemplateDef } from "./types";

export type { TemplateCategory, TemplateDef } from "./types";

/** Gallery order: broadest appeal first, then grouped by category below. */
export const TEMPLATES: TemplateDef[] = [
  leadMagnet,
  speedToLead,
  quizFunnel,
  newsletterWelcome,
  referralProgram,
  saasTrial,
  trialToDemo,
  churnSave,
  dunning,
  abandonedCart,
  postPurchase,
  productLaunch,
  backInStock,
  winback,
  birthdayClub,
  appointmentReminders,
  estimateToJob,
  reviewReferral,
  realEstate,
  webinar,
  workshopTickets,
  tradeShow,
  discoveryCall,
  courseOnboarding,
  agencyOnboarding,
  agencyRetention,
];

/**
 * Category display order for the gallery. Every category in TemplateCategory
 * must appear here — the gallery groups by this list, so a missing entry
 * would silently hide its templates.
 */
export const CATEGORY_ORDER: TemplateCategory[] = [
  "Lead gen",
  "SaaS",
  "E-commerce",
  "Services",
  "Events",
  "Coaching",
  "Agency",
];

/** Templates grouped for the gallery, in CATEGORY_ORDER. Empty groups dropped. */
export function templatesByCategory(): { category: TemplateCategory; items: TemplateDef[] }[] {
  return CATEGORY_ORDER.map((category) => ({
    category,
    items: TEMPLATES.filter((t) => t.category === category),
  })).filter((g) => g.items.length > 0);
}

export function getTemplate(slug: string): TemplateDef | null {
  return TEMPLATES.find((t) => t.slug === slug) ?? null;
}

/** Card/detail stats derived once from the flow. */
export function templateStats(t: TemplateDef) {
  const nodes = t.flow.nodes;
  return {
    nodeCount: nodes.length,
    flowCount: nodes.filter((n) => n.type === "flow").length,
    emailCount: nodes.filter((n) => n.data.kind === "auto-email").length,
    smsCount: nodes.filter((n) => n.data.kind === "auto-sms").length,
  };
}

/**
 * Up to `limit` other templates in the same category — the "related" strip on
 * a detail page, so no template is a dead end.
 */
export function relatedTemplates(slug: string, limit = 3): TemplateDef[] {
  const t = getTemplate(slug);
  if (!t) return [];
  const sameCategory = TEMPLATES.filter(
    (o) => o.slug !== slug && o.category === t.category
  );
  const rest = TEMPLATES.filter((o) => o.slug !== slug && o.category !== t.category);
  return [...sameCategory, ...rest].slice(0, limit);
}
